import { useParams, useNavigate } from 'react-router-dom';
import { useTeamStore } from '@/store/useTeamStore';
import { routes } from '@/lib/routes';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { MemberForm } from '@/tools/inner-team/components/team/MemberForm';
import { CharacterPortrait } from '@/tools/inner-team/components/team/CharacterPortrait';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Pencil, Trash2, RefreshCw, MessageSquare, ChevronDown, ChevronUp, Wand2, Music2 } from 'lucide-react';
import { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api, VoucherRequiredError } from '@/lib/api';
import { useChatStore } from '@/store/useChatStore';
import { useAuthStore } from '@/store/useAuthStore';
import { LoginPromptDialog } from '@/components/auth/LoginPromptDialog';
import { VoucherPromptDialog } from '@/components/auth/VoucherPromptDialog';
import { MusicSuggestDialog } from '@/tools/inner-team/components/team/MusicSuggestDialog';

export function TeamMemberDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { members, deleteMember, updateMember } = useTeamStore();
  const { sessions, createSession, setActiveSession } = useChatStore();
  const [editOpen, setEditOpen] = useState(false);
  const [generatingAvatar, setGeneratingAvatar] = useState(false);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [appearanceDraft, setAppearanceDraft] = useState('');
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [voucherPromptOpen, setVoucherPromptOpen] = useState(false);
  const [musicDialogOpen, setMusicDialogOpen] = useState(false);
  const [manualMusicMode, setManualMusicMode] = useState(false);
  const [manualUrl, setManualUrl] = useState('');

  const member = members.find((m) => m.id === id);
  if (!member) {
    return (
      <AppShell>
        <PageHeader title="Nicht gefunden" showBack />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          Dieses Teammitglied existiert nicht.
        </div>
      </AppShell>
    );
  }

  function handleConsult() {
    const session = createSession('member', [member!.id]);
    setActiveSession(session.id);
    navigate(routes.innerTeam.memberChat(session.id));
  }

  async function handleDelete() {
    try {
      await deleteMember(member!.id);
      toast.success(`${member!.name} wurde entfernt.`);
      navigate(routes.innerTeam.teamDashboard());
    } catch (err) {
      toast.error(`Löschen fehlgeschlagen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
    }
  }

  function handleOpenAppearance() {
    setAppearanceDraft(member!.appearanceDescription ?? '');
    setAppearanceOpen(true);
  }

  async function handleGenerateFromDraft() {
    if (!useAuthStore.getState().user) { setLoginPromptOpen(true); return; }
    if (!appearanceDraft.trim()) return;
    setGeneratingAvatar(true);
    try {
      const germanDesc = appearanceDraft.trim();
      const { avatarImageUrl } = await api.avatar.generate(member!.id, germanDesc);
      updateMember(member!.id, { avatarImageUrl, appearanceDescription: germanDesc });
      setAppearanceOpen(false);
      toast.success('Avatar wurde generiert!');
    } catch (err) {
      if (err instanceof VoucherRequiredError) {
        setVoucherPromptOpen(true);
      } else {
        toast.error('Avatar-Generierung fehlgeschlagen.');
      }
    } finally {
      setGeneratingAvatar(false);
    }
  }

  async function handleRegenerateAvatar() {
    if (!useAuthStore.getState().user) { setLoginPromptOpen(true); return; }
    setGeneratingAvatar(true);
    try {
      const linkedSession = sessions
        .filter((s) => s.memberIds.includes(member!.id))
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
      const result = await api.avatar.regenerate(member!.id, linkedSession?.id);
      updateMember(member!.id, {
        avatarImageUrl: result.avatarImageUrl,
        appearanceDescription: result.appearanceDescription,
      });
      toast.success('Avatar wurde neu generiert!');
    } catch (err) {
      if (err instanceof VoucherRequiredError) {
        setVoucherPromptOpen(true);
      } else {
        toast.error('Avatar-Generierung fehlgeschlagen.');
      }
    } finally {
      setGeneratingAvatar(false);
    }
  }

  function extractSpotifyTrackId(input: string): string | null {
    const trimmed = input.trim();
    const urlMatch = trimmed.match(/open\.spotify\.com\/track\/([a-zA-Z0-9]+)/);
    if (urlMatch) return urlMatch[1]!;
    const uriMatch = trimmed.match(/^spotify:track:([a-zA-Z0-9]+)$/);
    if (uriMatch) return uriMatch[1]!;
    if (/^[a-zA-Z0-9]{22}$/.test(trimmed)) return trimmed;
    return null;
  }

  function handlePickSuggestedTrack(trackId: string) {
    updateMember(member!.id, { spotifyTrackId: trackId });
    toast.success('Song hinzugefügt');
  }

  function handleSaveManualUrl() {
    const trackId = extractSpotifyTrackId(manualUrl);
    if (!trackId) {
      toast.error('Keine gültige Spotify-Track-URL');
      return;
    }
    updateMember(member!.id, { spotifyTrackId: trackId });
    setManualMusicMode(false);
    setManualUrl('');
    toast.success('Song hinzugefügt');
  }

  function handleRemoveTrack() {
    if (!confirm('Song wirklich entfernen?')) return;
    updateMember(member!.id, { spotifyTrackId: undefined });
    toast.success('Song entfernt');
  }

  function handleMusicSuggestClick() {
    if (!useAuthStore.getState().user) { setLoginPromptOpen(true); return; }
    setMusicDialogOpen(true);
  }

  return (
    <AppShell>
      <PageHeader
        title={member.name}
        showBack
        right={
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogTrigger
              render={<Button variant="ghost" size="icon" aria-label="Bearbeiten" />}
            >
              <Pencil size={18} />
            </DialogTrigger>
            <DialogContent className="max-h-[90dvh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Teammitglied bearbeiten</DialogTitle>
              </DialogHeader>
              <MemberForm
                initial={member}
                onSave={() => {
                  setEditOpen(false);
                  toast.success('Änderungen gespeichert');
                }}
                onCancel={() => setEditOpen(false)}
              />
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex-1 overflow-y-auto px-4 py-6 space-y-6">
        {/* Avatar + Name */}
        <div className="flex flex-col items-center gap-3 pt-2">
          <div className="relative">
            <CharacterPortrait
                emoji={member.avatarEmoji}
                color={member.avatarColor}
                avatarImageUrl={member.avatarImageUrl}
                size="lg"
                onImageError={() => updateMember(member.id, { avatarImageUrl: undefined })}
              />
            {generatingAvatar && (
              <div className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center">
                <RefreshCw size={24} className="text-white animate-spin" />
              </div>
            )}
          </div>
          <div className="text-center">
            <p className="text-xl font-semibold">{member.name}</p>
            <p className="text-sm text-muted-foreground">{member.role}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRegenerateAvatar}
              disabled={generatingAvatar}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              <RefreshCw size={12} className={generatingAvatar ? 'animate-spin' : ''} />
              {member.avatarImageUrl ? 'Neu generieren' : 'Generieren'}
            </button>
            <span className="text-muted-foreground/30 text-xs">|</span>
            <button
              onClick={appearanceOpen ? () => setAppearanceOpen(false) : handleOpenAppearance}
              disabled={generatingAvatar}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              <Wand2 size={12} />
              Aussehen anpassen
              {appearanceOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>

          {appearanceOpen && (
            <div className="w-full max-w-sm space-y-2 mt-1">
              <p className="text-xs text-muted-foreground text-center">
                Beschreibe das Aussehen auf Deutsch — Alter, Geschlecht, Haare, Ausdruck, Kleidung.
              </p>
              <Textarea
                value={appearanceDraft}
                onChange={(e) => setAppearanceDraft(e.target.value)}
                placeholder="z.B. strenge Frau mittleren Alters, kurze graue Haare, scharfe Augen, dunkles Jackett..."
                rows={3}
                className="text-xs resize-none"
              />
              <Button
                size="sm"
                className="w-full gap-1.5"
                onClick={handleGenerateFromDraft}
                disabled={generatingAvatar || !appearanceDraft.trim()}
              >
                <Wand2 size={13} />
                {generatingAvatar ? 'Generiere…' : 'Avatar generieren'}
              </Button>
            </div>
          )}
        </div>

        {/* Sound */}
        <section className="rounded-xl border border-border px-4 py-3 space-y-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Music2 size={16} /> Musik
          </h3>

          {member.spotifyTrackId ? (
            <>
              <iframe
                src={`https://open.spotify.com/embed/track/${member.spotifyTrackId}?utm_source=innerteam`}
                width="100%"
                height="80"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="rounded-lg"
                title="Spotify Player"
              />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleMusicSuggestClick}>
                  Anderen Song wählen
                </Button>
                <Button size="sm" variant="ghost" onClick={handleRemoveTrack}>
                  Entfernen
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-muted-foreground">
                Gib diesem Anteil eine Stimme — ein Song, der für dich zu ihm passt.
              </p>
              {!manualMusicMode ? (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={handleMusicSuggestClick} className="gap-1.5">
                    <Music2 size={14} />
                    Song vorschlagen lassen
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setManualMusicMode(true)}>
                    Spotify-Link einfügen
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Input
                    value={manualUrl}
                    onChange={(e) => setManualUrl(e.target.value)}
                    placeholder="https://open.spotify.com/track/..."
                    className="text-xs font-mono"
                  />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => { setManualMusicMode(false); setManualUrl(''); }}>
                      Abbrechen
                    </Button>
                    <Button size="sm" onClick={handleSaveManualUrl} disabled={!manualUrl.trim()}>
                      Speichern
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        {/* Description */}
        <section className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Beschreibung
          </h3>
          <p className="text-sm leading-relaxed">{member.description}</p>
        </section>

        {/* Values */}
        {member.values.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Werte
            </h3>
            <div className="flex flex-wrap gap-2">
              {member.values.map((v) => (
                <Badge key={v} variant="secondary">
                  {v}
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* Typical phrases */}
        {member.typicalPhrases.length > 0 && (
          <section className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Typische Sätze
            </h3>
            <ul className="space-y-2">
              {member.typicalPhrases.map((phrase, i) => (
                <li key={i} className="text-sm italic text-foreground/80 pl-3 border-l-2 border-primary/30">
                  „{phrase}"
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Actions */}
        <div className="pt-4 border-t border-border flex flex-col gap-3">
          <Button
            size="sm"
            className="gap-2 w-full"
            onClick={handleConsult}
          >
            <MessageSquare size={15} />
            Befragen
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="gap-2"
            onClick={handleDelete}
          >
            <Trash2 size={15} />
            Aus dem Team entfernen
          </Button>
        </div>
      </div>
      <LoginPromptDialog open={loginPromptOpen} onOpenChange={setLoginPromptOpen} />
      <VoucherPromptDialog open={voucherPromptOpen} onOpenChange={setVoucherPromptOpen} />
      <MusicSuggestDialog
        open={musicDialogOpen}
        onOpenChange={setMusicDialogOpen}
        memberId={member.id}
        memberName={member.name}
        onPick={handlePickSuggestedTrack}
        onVoucherRequired={() => setVoucherPromptOpen(true)}
      />
    </AppShell>
  );
}
